import type {
  StaffItineraryDetail,
  StaffItineraryRow,
  StaffItineraryValidation,
} from "@lafayette/schemas";

import {
  formatDay,
  formatOffset,
  localPartsAt,
  minutesBetween,
  offsetMinutesAt,
  repeatedChoiceOf,
} from "./time";

// The plan as the page shows it: days, items in time order, local times in each item's own zone, the gap between items, the issues
// of each item, and how fresh the checks are. Pure functions over the staff detail (MD-I8): no fetch, no clock except the `now` a
// caller passes. The wall times come from the server (`item.view.localStart`, `localEnd`): the page does not convert a stored instant
// to a wall time. It probes the zone only for a hint (the UTC offset label, a repeated clock time) that the server confirms or refuses.

export type Detail = StaffItineraryDetail;
export type Item = Detail["items"][number];
export type Section = Detail["sections"][number];
export type Issue = Detail["issues"][number];
export type Report = NonNullable<Detail["report"]>;
/** The read model of an item. A server older than this page does not send it (`view` is optional in the client schema, review F4). */
export type ItemRead = NonNullable<Item["view"]>;
export type LinkedRequest = ItemRead["requests"][number];

/**
 * The display wall times of an item: the server's (`view`), else, for a server that sends no `view`, the stored instant read in the
 * item's own zone. That fallback is for display only: nothing the page sends is built from it.
 */
export function wallOf(item: Item): Pick<ItemRead, "localStart" | "localEnd" | "endTimeZone"> {
  if (item.view !== undefined) return item.view;
  const read = (iso: string | null, zone: string | null) => {
    if (iso === null || zone === null) return null;
    const parts = localPartsAt(Date.parse(iso), zone);
    return `${parts.date}T${parts.time}`;
  };
  const end =
    item.endsAt ??
    (item.startsAt !== null && item.durationMinutes !== null
      ? new Date(Date.parse(item.startsAt) + item.durationMinutes * 60_000).toISOString()
      : null);
  const endZone = item.endTimeZone ?? item.timeZone;
  return {
    localStart: read(item.startsAt, item.timeZone),
    localEnd: read(end, endZone),
    endTimeZone: endZone,
  };
}

/** Some item has no read model: the server is older than this page. The page says so and shows no evidence, request or catalog detail. */
export const detailsMissing = (detail: Detail): boolean =>
  detail.items.some((item) => item.view === undefined);
/** The answer of a full validation run (MD-I12 `run`): routes, weather and locations as the providers answered for this version. */
export type RunView = NonNullable<StaffItineraryValidation["run"]>;

export type RunItem = Readonly<{
  /** The travel leg INTO the item. */
  edgeIn: RunView["edges"][number] | null;
  weather: RunView["weather"][number] | null;
  /** The issues of the run about this item (location, travel, weather), by stable id. */
  issues: RunView["issues"];
  /** A severe forecast: a concierge reviews it. */
  review: boolean;
}>;

/** What a full run says about each item, by stable id (the lineage id of the item). */
export function runItemsOf(run: RunView): ReadonlyMap<string, RunItem> {
  const out = new Map<
    string,
    {
      edgeIn: RunItem["edgeIn"];
      weather: RunItem["weather"];
      issues: RunView["issues"][number][];
      review: boolean;
    }
  >();
  const slot = (id: string) => {
    const known = out.get(id);
    if (known !== undefined) return known;
    const fresh = { edgeIn: null, weather: null, issues: [], review: false };
    out.set(id, fresh);
    return fresh;
  };
  for (const edge of run.edges) if (edge.toStableId !== null) slot(edge.toStableId).edgeIn = edge;
  for (const weather of run.weather) slot(weather.stableId).weather = weather;
  for (const issue of run.issues)
    if (issue.stableId !== null) slot(issue.stableId).issues.push(issue);
  for (const id of run.conciergeReview) slot(id).review = true;
  return out;
}

export type LocalPoint = Readonly<{
  date: string;
  time: string;
  zone: string;
  offsetLabel: string;
}>;

export type ItemView = Readonly<{
  item: Item;
  /** The id of the page anchor of the item (`item-<row id>`): an issue links to it. */
  anchor: string;
  start: LocalPoint | null;
  end: LocalPoint | null;
  /** The end is read in another zone than the start (a flight). */
  crossZone: boolean;
  /** The end falls on another local date than the start (read in each own zone): the page shows that date. */
  endsOnOtherDate: boolean;
  durationMinutes: number | null;
  /** Minutes between the end of the previous timed item of the day and this start. Negative: they overlap. `null`: unknown. */
  gapBeforeMinutes: number | null;
  /** The clock time repeats on this day (daylight saving overlap) and which of the two this item is. */
  repeated: "earlier" | "later" | null;
  /** The same for the end, read in the end zone: a one hour item that spans the clock change starts and ends at "01:30". */
  endRepeated: "earlier" | "later" | null;
  /** The read model of the item, or `null` when the server did not send one. */
  read: ItemRead | null;
  /** Staff acknowledged the travel gap into this item, and the newest report of this version says so (review F2). */
  acknowledged: boolean;
  issues: readonly Issue[];
  /** The travel time INTO this item is something the route check judges (a timed item after another timed item of the day). */
  routeEligible: boolean;
  /** The forecast matters for this item (it reacts to weather and is not cancelled). */
  weatherEligible: boolean;
}>;

export type DayView = Readonly<{
  section: Section;
  heading: string;
  items: readonly ItemView[];
}>;

const anchorOf = (item: Item) => `item-${item.id}`;

/** A wall time the server computed (`YYYY-MM-DDTHH:MM`) with the zone it is read in; the offset label is a probe of the instant. */
function pointOf(local: string | null, iso: string | null, zone: string | null): LocalPoint | null {
  if (local === null || iso === null || zone === null) return null;
  return {
    date: local.slice(0, 10),
    time: local.slice(11, 16),
    zone,
    offsetLabel: formatOffset(offsetMinutesAt(Date.parse(iso), zone)),
  };
}

function endIso(item: Item): string | null {
  if (item.endsAt !== null) return item.endsAt;
  if (item.startsAt !== null && item.durationMinutes !== null) {
    return new Date(Date.parse(item.startsAt) + item.durationMinutes * 60_000).toISOString();
  }
  return null;
}

const order = (item: Item): number =>
  item.timingKind === "ALL_DAY" ? 0 : item.timingKind === "SCHEDULED" ? 1 : 2;

function sortItems(items: readonly Item[]): Item[] {
  return [...items].sort((a, b) => {
    const rank = order(a) - order(b);
    if (rank !== 0) return rank;
    if (a.startsAt !== null && b.startsAt !== null && a.startsAt !== b.startsAt) {
      return a.startsAt < b.startsAt ? -1 : 1;
    }
    return a.position - b.position;
  });
}

export function dayHeading(section: Section): string {
  if (section.kind === "FLEXIBLE") return section.title ?? "Not on a day yet";
  const date = section.localDate === null ? "Undated day" : formatDay(section.localDate);
  return section.title === null || section.title === "" ? date : `${date} · ${section.title}`;
}

/** Each issue with a key that is stable for the list: the same code on the same target gets a running number. */
export function keyedIssues(
  issues: readonly Issue[],
): readonly Readonly<{ key: string; issue: Issue }>[] {
  const seen = new Map<string, number>();
  return issues.map((issue) => {
    const base = `${issue.code}:${issue.itemId ?? issue.sectionId ?? "plan"}`;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return { key: `${base}:${n}`, issue };
  });
}

export function buildDays(detail: Detail): readonly DayView[] {
  const acknowledgedIds = new Set(
    currentReport(detail)?.issueCodes.find(
      (entry) => entry.code === ACKNOWLEDGEABLE_CODE && entry.severity === "BLOCKING",
    )?.acknowledgedStableIds ?? [],
  );
  const issuesByItem = new Map<string, Issue[]>();
  for (const issue of detail.issues) {
    if (issue.itemId === null) continue;
    issuesByItem.set(issue.itemId, [...(issuesByItem.get(issue.itemId) ?? []), issue]);
  }
  return [...detail.sections]
    .sort((a, b) => a.position - b.position)
    .map((section) => {
      let previousEnd: string | null = null;
      const items = sortItems(detail.items.filter((item) => item.sectionId === section.id)).map(
        (item): ItemView => {
          const zone = item.timeZone;
          const endZone = item.endTimeZone ?? zone;
          const finish = endIso(item);
          const wall = wallOf(item);
          const start = pointOf(wall.localStart, item.startsAt, zone);
          const end = pointOf(wall.localEnd, finish, wall.endTimeZone ?? endZone);
          const duration =
            item.durationMinutes ??
            (item.startsAt !== null && finish !== null
              ? minutesBetween(item.startsAt, finish)
              : null);
          const gap =
            item.timingKind === "SCHEDULED" && item.startsAt !== null && previousEnd !== null
              ? minutesBetween(previousEnd, item.startsAt)
              : null;
          if (item.timingKind === "SCHEDULED") previousEnd = finish;
          return {
            item,
            anchor: anchorOf(item),
            start,
            end,
            crossZone: start !== null && end !== null && start.zone !== end.zone,
            endsOnOtherDate: start !== null && end !== null && end.date !== start.date,
            durationMinutes: duration,
            gapBeforeMinutes: gap,
            repeated:
              item.startsAt !== null && zone !== null
                ? repeatedChoiceOf(Date.parse(item.startsAt), zone)
                : null,
            endRepeated:
              finish !== null && endZone !== null
                ? repeatedChoiceOf(Date.parse(finish), endZone)
                : null,
            read: item.view ?? null,
            acknowledged: acknowledgedIds.has(item.lineageId),
            issues: issuesByItem.get(item.id) ?? [],
            routeEligible: false,
            weatherEligible: item.weatherSensitivity !== "NONE" && item.itemStatus !== "CANCELLED",
          };
        },
      );
      // A route is judged between two consecutive timed items that are not travel or buffer themselves.
      let seenPlace = false;
      const marked = items.map((view) => {
        const place =
          view.item.timingKind === "SCHEDULED" &&
          view.item.itemStatus !== "CANCELLED" &&
          view.item.kind !== "TRANSIT" &&
          view.item.kind !== "BUFFER";
        const routeEligible = place && seenPlace;
        if (place) seenPlace = true;
        return routeEligible ? { ...view, routeEligible } : view;
      });
      return { section, heading: dayHeading(section), items: marked };
    });
}

// --- revisions ---------------------------------------------------------------------------------------------------------------

/** The plan of record, else the newest draft, else the newest row: the itinerary the page opens first. */
export function defaultRevision(rows: readonly StaffItineraryRow[]): StaffItineraryRow | null {
  const newest = (list: readonly StaffItineraryRow[]) =>
    [...list].sort((a, b) => b.revision - a.revision || (b.createdAt < a.createdAt ? -1 : 1))[0] ??
    null;
  return (
    newest(rows.filter((row) => row.status === "ACTIVE")) ??
    newest(rows.filter((row) => row.status === "DRAFT")) ??
    newest(rows)
  );
}

export const activeRevision = (rows: readonly StaffItineraryRow[]): StaffItineraryRow | null =>
  rows.find((row) => row.status === "ACTIVE") ?? null;

// --- issues and the activation gate ------------------------------------------------------------------------------------------

/** The one code staff may acknowledge (contract section 3.4). */
export const ACKNOWLEDGEABLE_CODE = "TRAVEL_TIME_EXCEEDS_GAP";
export const MIN_ACKNOWLEDGE_REASON = 10;

/** The report counts for this version of the plan only: an older report describes content that has changed. */
export function currentReport(detail: Detail): Report | null {
  return detail.report !== null && detail.report.itineraryVersion === detail.itinerary.version
    ? detail.report
    : null;
}

export type ActivationGate = Readonly<{
  blockingCodes: readonly string[];
  /** Only an evidence issue `TRAVEL_TIME_EXCEEDS_GAP` blocks: staff may acknowledge it with a reason. */
  needsAcknowledgement: boolean;
  /** A blocking code that staff cannot acknowledge. */
  blocked: boolean;
}>;

/**
 * What stops an activation, by the rule of the server (`planItineraryTransition` and `acknowledgementOf`, itinerary-engine.md 1.13):
 * a BLOCKING issue of the structural validation (`detail.issues`, the cross-city minimum included) refuses every activation and is
 * never acknowledgeable. Only a `TRAVEL_TIME_EXCEEDS_GAP` that came from evidence can be acknowledged. The newest report adds the
 * structural issues too, so an evidence issue is what the report counts beyond the structural ones of the same code.
 */
export function activationGate(detail: Detail): ActivationGate {
  const structural = new Map<string, number>();
  for (const issue of detail.issues) {
    if (issue.severity === "BLOCKING")
      structural.set(issue.code, (structural.get(issue.code) ?? 0) + 1);
  }
  const evidence = new Set<string>();
  for (const entry of currentReport(detail)?.issueCodes ?? []) {
    if (entry.severity === "BLOCKING" && entry.count > (structural.get(entry.code) ?? 0)) {
      evidence.add(entry.code);
    }
  }
  const blockingCodes = [...new Set([...structural.keys(), ...evidence])].sort();
  const evidenceCodes = [...evidence];
  return {
    blockingCodes,
    needsAcknowledgement:
      structural.size === 0 &&
      evidenceCodes.length > 0 &&
      evidenceCodes.every((code) => code === ACKNOWLEDGEABLE_CODE),
    blocked: structural.size > 0 || evidenceCodes.some((code) => code !== ACKNOWLEDGEABLE_CODE),
  };
}

/** The version token of the plan a page read: a dialog keeps the one it was opened on and compares it with the plan read now. */
export const planToken = (detail: Detail): string =>
  `${detail.itinerary.id}:${detail.itinerary.status}:${detail.itinerary.revision}:${detail.itinerary.version}`;

// --- evidence freshness ------------------------------------------------------------------------------------------------------

export type EvidenceState = "VERIFIED" | "UNVERIFIED" | "STALE";

export type EvidencePart = Readonly<{
  /** `null`: there is nothing to check (no travel between two timed items, no item that reacts to weather). */
  state: EvidenceState | null;
  note: string;
  verified: number;
  stale: number;
  unverified: number;
}>;

export type EvidenceView = Readonly<{
  /** The newest observation among the stored readings of the items, or `null` when nothing was ever read. */
  observedAt: string | null;
  ageMinutes: number | null;
  /** The newest validation report was written for an older version of the plan. */
  reportOlderVersion: boolean;
  /** When the newest validation report was written, or `null`. */
  reportCheckedAt: string | null;
  route: EvidencePart;
  weather: EvidencePart;
}>;

/**
 * What the STORED readings of the items say, item by item (MD-I8 `view.evidence`). A reading has its own observation time and its own
 * freshness (the server judged it against the plan as it is now), so this is not the age of a report. A structural check by staff writes
 * a report and no reading: it never changes these numbers. An item the check does not apply to is not counted.
 */
export function evidenceOf(detail: Detail, days: readonly DayView[], now: Date): EvidenceView {
  const tally = (
    views: readonly ItemView[],
    pick: (view: ItemView) => Readonly<{ state: EvidenceState; observedAt: string | null }>,
    nothing: string,
  ): EvidencePart => {
    if (views.length === 0) {
      return { state: null, note: nothing, verified: 0, stale: 0, unverified: 0 };
    }
    let verified = 0;
    let stale = 0;
    let unverified = 0;
    for (const view of views) {
      const state = pick(view).state;
      if (state === "VERIFIED") verified += 1;
      else if (state === "STALE") stale += 1;
      else unverified += 1;
    }
    const words = [
      verified > 0 ? `${verified} verified` : null,
      stale > 0 ? `${stale} stale` : null,
      unverified > 0 ? `${unverified} not checked or not verified` : null,
    ].filter((part): part is string => part !== null);
    return {
      state: unverified > 0 ? "UNVERIFIED" : stale > 0 ? "STALE" : "VERIFIED",
      note: `${words.join(", ")}.`,
      verified,
      stale,
      unverified,
    };
  };
  // An item the server sent no read model for is not counted: there is nothing to read.
  const missing = detailsMissing(detail);
  const all = days.flatMap((day) => day.items).filter((view) => view.read !== null);
  const route = tally(
    all.filter((view) => view.routeEligible),
    (view) => (view.read as ItemRead).evidence.route,
    missing
      ? "Not available from this server version."
      : "No travel between two timed items to check.",
  );
  const weather = tally(
    all.filter((view) => view.weatherEligible),
    (view) => (view.read as ItemRead).evidence.weather,
    missing ? "Not available from this server version." : "No item reacts to weather.",
  );
  // Only the readings of an item the check applies to count: a leftover reading of an item that is not judged says nothing about the plan.
  const times = all.flatMap((view) => {
    const reads = (view.read as ItemRead).evidence;
    return [
      view.routeEligible ? reads.route.observedAt : null,
      view.weatherEligible ? reads.weather.observedAt : null,
    ].flatMap((at) => (at === null ? [] : [Date.parse(at)]));
  });
  const newest = times.length === 0 ? null : new Date(Math.max(...times));
  const report = detail.report;
  return {
    observedAt: newest === null ? null : newest.toISOString(),
    ageMinutes:
      newest === null ? null : Math.max(0, Math.round((now.getTime() - newest.getTime()) / 60_000)),
    reportOlderVersion: report !== null && report.itineraryVersion !== detail.itinerary.version,
    reportCheckedAt: report === null ? null : report.checkedAt,
    route,
    weather,
  };
}

/** `95` as `1 h 35 min ago`. */
export function ageText(minutes: number): string {
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 48 * 60) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${hours} h ago` : `${hours} h ${rest} min ago`;
  }
  return `${Math.floor(minutes / (24 * 60))} days ago`;
}

// --- references --------------------------------------------------------------------------------------------------------------

/** Every request linked to an item of the plan (by lineage, open and closed), once. */
export const requestIdsOf = (items: readonly Item[]): readonly string[] => [
  ...new Set(
    items.flatMap((item) => (item.view?.requests ?? []).map((request) => request.requestId)),
  ),
];

/**
 * The page knows an edit is consequential when the item is fixed or carries a request. A linked open request or a payment is known
 * only to the server, which answers `itinerary.requires_confirmation` or `itinerary.item_linked`: the dialog then asks for a reason.
 */
export const touchesConsequentialItem = (item: Item): boolean =>
  item.isFixed || item.conciergeRequestId !== null;
